'use client';

import { useCallback, useEffect, useState } from 'react';
import { Button, Card, EmptyState, Eyebrow, Info, Pill } from '@/app/(dashboard)/dashboard/components/controls';
import { Modal } from '@/app/(dashboard)/dashboard/components/modal';
import { CAST, characterById, displayNameFor } from '@/lib/heckle-cast';
import { view } from '@/lib/heckle-presentation';
import { useRefreshScheduler } from './refresh-scheduler';

const PLATFORM_LABEL = { twitch: 'Twitch', youtube: 'YouTube', tiktok: 'TikTok' };
const CONFIRM_PHRASE = 'delete my data';

// §8.6 two-cadence refresh (E5 follow-up, owner test 2026-07-21): while an
// event is LIVE the full state re-fetches every 5s (tallies/lock/results);
// while IDLE the page probes the skeletal /api/viewer/polls-open every 10s
// so a poll created after page-load appears without a manual reload. Both
// pause while the tab is hidden (a backgrounded phone tab costs nothing);
// tabbing back refreshes immediately.
const LIVE_REFRESH_MS = 5000;
const IDLE_PROBE_MS = 10000;

async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

/**
 * The signed-in /viewer page (E4, PRD §8; owner layout revision 2026-07-22:
 * a slim channel-switcher BAND under the identity header — wallet growth
 * lives there — then Catch | Your Character (neither grows with channel
 * count), Collection below; supersedes the 2026-07-20 purse-card row). One
 * client component hydrated with the server-assembled state; refreshes
 * through GET /api/viewer/state (the same buildViewerState). Every
 * viewer-controlled string (displayName, channelName, usernames) renders as
 * JSX TEXT NODES only — never markup, styles, or URLs (PRD §8.1 security
 * note).
 */
export default function ViewerClient({
  identity,
  identities,
  linkPending,
  initialState,
  errorMessage,
  tiktokLoginEnabled,
}) {
  const [state, setState] = useState(initialState);
  // Smart default at LOAD only (switcher rework 2026-07-22): open on the
  // channel the server detected the viewer watching (presence-based
  // suggestion), else the first (highest-balance) wallet. Refreshes keep a
  // still-valid selection — the page never re-selects under the viewer.
  const [selectedChannel, setSelectedChannel] = useState(
    initialState?.suggestedChannelUserId ?? initialState?.wallets?.[0]?.channelUserId ?? null,
  );
  const [reveal, setReveal] = useState(null); // { characterKey, revealed }
  const [charBusy, setCharBusy] = useState(false);
  const [charError, setCharError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/viewer/state');
      if (!res.ok) return;
      const data = await res.json();
      if (data?.identity) {
        setState({
          wallets: data.wallets,
          collection: data.collection,
          config: data.config,
          defaultCharacterKey: data.defaultCharacterKey ?? null,
          activePolls: data.activePolls ?? [],
          pollStats: data.pollStats ?? null,
          suggestedChannelUserId: data.suggestedChannelUserId ?? null,
        });
        setSelectedChannel((prev) =>
          prev && data.wallets?.some((w) => w.channelUserId === prev)
            ? prev
            : (data.suggestedChannelUserId ?? data.wallets?.[0]?.channelUserId ?? null),
        );
      }
    } catch {
      // Transient network failure — the next refresh (or manual action) retries.
    }
  }, []);

  // §8.6 two-cadence refresh (fetch-based — no socket; the page is a
  // cross-channel identity surface, not a room member). The cadence +
  // visibility state machine lives in ./refresh-scheduler (behaviorally
  // tested): LIVE → full state every LIVE_REFRESH_MS; IDLE → this skeletal
  // probe every IDLE_PROBE_MS, promoting to a refresh the moment something
  // is live; hidden tabs pause entirely. The refresh that reports an ENDED
  // event is also the one that shows its result/refund.
  const probeOpen = useCallback(async () => {
    const res = await fetch('/api/viewer/polls-open');
    if (!res.ok) return false;
    const data = await res.json();
    return data?.open === true;
  }, []);
  const pollsActive = (state?.activePolls?.length ?? 0) > 0;
  useRefreshScheduler({
    refresh,
    probe: probeOpen,
    live: pollsActive,
    liveMs: LIVE_REFRESH_MS,
    idleMs: IDLE_PROBE_MS,
  });

  // ONE character-selection path for the grid, the Randomize button, and the
  // back-to-auto-assigned action (all write through /api/viewer/lock).
  const selectCharacter = useCallback(
    async (characterKey) => {
      setCharBusy(true);
      setCharError(null);
      const { ok, data } = await postJson('/api/viewer/lock', { characterKey });
      setCharBusy(false);
      if (ok && data.ok) refresh();
      else if (data.reason === 'not_owned') setCharError('You don’t own that character yet.');
      else setCharError('Could not update your character — try again.');
    },
    [refresh],
  );

  // TikTok authed but not chat-linked, and nothing else signed in → the page
  // STILL renders (owner ruling 2026-08-03: the chat-link step must never
  // gate the door). identity stays null — nothing server-side exists to read
  // or delete until the bind promotes the session — so the header shows the
  // pending handle, the inline link card explains, DangerZone hides, and
  // the empty-wallet machinery ("No points yet") covers the body.

  const wallets = state?.wallets ?? [];
  const collection = state?.collection ?? null;
  const config = state?.config ?? { pullSp: 1000, starterPullSp: 300, castTotal: CAST.length };
  const ownedSet = new Set((collection?.entries ?? []).map((e) => e.characterKey));
  const selectedKey = collection?.lockedCharacterKey ?? null;
  const defaultKey = state?.defaultCharacterKey ?? null;
  const ownedCount = collection?.ownedCount ?? 0;
  const complete = ownedCount >= config.castTotal;
  const price = collection?.starterPullUsed ? config.pullSp : config.starterPullSp;
  const isStarter = !collection?.starterPullUsed;
  const selectedWallet = wallets.find((w) => w.channelUserId === selectedChannel) || null;

  return (
    <main className="mx-auto w-full max-w-4xl space-y-6 px-4 py-10">
      <IdentityHeader
        identity={identity}
        pending={linkPending}
        identities={identities}
        tiktokLoginEnabled={tiktokLoginEnabled}
      />

      {errorMessage && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">{errorMessage}</div>
      )}

      {linkPending && (
        <Card eyebrow="TikTok link" title="Finish linking your TikTok">
          <TikTokLinkScreen username={linkPending.username} />
        </Card>
      )}

      {/* Switcher-band layout (owner mockup-picked 2026-07-22, superseding
          the 2026-07-20 purse-card row): channel selection is a slim
          dropdown band — wallet growth lives there, horizontally — and the
          top row pairs Catch with Your Character, two cards whose heights
          no longer depend on channel count, so the pairing stays balanced. */}
      {/* Keyed on the single/multi boundary: crossing it REMOUNTS the
          switcher, resetting its open state — a refresh that shrinks the
          wallets while the menu is open must not strand a full-viewport
          backdrop swallowing the next click (test-review find 2026-07-22),
          and growing back must not pop the menu unbidden. */}
      <ChannelSwitcher
        key={wallets.length > 1 ? 'multi' : 'single'}
        wallets={wallets}
        selectedChannel={selectedChannel}
        suggestedChannel={state?.suggestedChannelUserId ?? null}
        onSelect={setSelectedChannel}
      />

      {/* items-stretch + h-full cards: the pairing is equal-height by
          construction (owner find 2026-07-22 — items-start left a visible
          gap under the shorter card). */}
      <div className="grid items-stretch gap-6 md:grid-cols-2">
        <PullSurface
          price={price}
          fullPrice={config.pullSp}
          isStarter={isStarter}
          complete={complete}
          selectedWallet={selectedWallet}
          onCaught={(result) => {
            setReveal(result);
            refresh();
          }}
        />
        <YourCharacter
          ownedSet={ownedSet}
          selectedKey={selectedKey}
          defaultKey={defaultKey}
          busy={charBusy}
          error={charError}
          onSelect={selectCharacter}
        />
      </div>

      <CollectionGrid
        ownedSet={ownedSet}
        selectedKey={selectedKey}
        ownedCount={ownedCount}
        castTotal={config.castTotal}
        busy={charBusy}
        onSelect={selectCharacter}
      />

      {/* §8.6 active polls/gambas (E5) — chat commands stay the primary
          path; this is the no-typing alternative. */}
      {(state?.activePolls?.length ?? 0) > 0 && (
        <div className="space-y-4">
          {state.activePolls.map((p) => (
            <ActivePollCard key={p.pollId} poll={p} onActed={refresh} />
          ))}
        </div>
      )}

      {state?.pollStats && (state.pollStats.wins + state.pollStats.losses > 0 || state.pollStats.pollsVoted > 0) ? (
        <YourRecord stats={state.pollStats} />
      ) : null}

      {identity && <DangerZone platform={identity.platform} />}

      {reveal && (
        <RevealModal characterKey={reveal.characterKey} revealed={reveal.revealed} onClose={() => setReveal(null)} />
      )}
    </main>
  );
}

function IdentityHeader({ identity, pending, identities, tiktokLoginEnabled }) {
  const [busy, setBusy] = useState(false);
  const activePlatform = identity?.platform ?? null;

  // ONE chip per platform, fixed order, each showing its live state — the
  // old others/addable pair rendered only fragments of the picture (owner
  // find 2026-08-03: a pending TikTok vanished from the header entirely, and
  // "Switch to X" rows read as an incomplete platform list). The pending
  // branch precedes addable, so "Add TikTok" can never render mid-link (it
  // would just restart the OAuth that put the viewer here).
  const chips = ['twitch', 'youtube', 'tiktok']
    .map((p) => {
      if (p === activePlatform) return { p, kind: 'active' };
      if (identities.includes(p)) return { p, kind: 'linked' };
      if (p === 'tiktok' && pending) return { p, kind: 'pending' };
      if (p === 'tiktok' && !tiktokLoginEnabled) return null;
      return { p, kind: 'addable' };
    })
    .filter(Boolean);

  async function switchTo(platform) {
    setBusy(true);
    const { ok } = await postJson('/api/viewer/switch', { platform });
    if (ok) window.location.reload();
    else setBusy(false);
  }
  async function signOut() {
    setBusy(true);
    await postJson('/api/viewer/logout');
    window.location.href = '/viewer';
  }

  return (
    <header className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Eyebrow>Shoosh viewers</Eyebrow>
          <h1 className="mt-2 text-2xl font-bold text-gum-black dark:text-white">Your collection</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {chips.map(({ p, kind }) => {
            if (kind === 'active') {
              return (
                <Pill key={p} tone="info">
                  {PLATFORM_LABEL[p]} · {identity.displayName}
                </Pill>
              );
            }
            if (kind === 'linked') {
              return (
                <Button key={p} variant="secondary" size="sm" disabled={busy} onClick={() => switchTo(p)}>
                  Switch to {PLATFORM_LABEL[p]}
                </Button>
              );
            }
            if (kind === 'pending') {
              return (
                <span
                  key={p}
                  className="rounded-full border border-dashed border-gum-gray-400 px-3 py-1 text-xs font-semibold text-gum-gray-500 dark:border-gum-gray-600 dark:text-gum-gray-400"
                >
                  {PLATFORM_LABEL[p]} · @{pending.username} — linking
                </span>
              );
            }
            return (
              <a
                key={p}
                href={`/api/auth/viewer/${p}/login`}
                className="rounded-lg border border-gum-gray-300 px-3 py-1.5 text-xs font-semibold text-gum-gray-700 hover:bg-gum-gray-100 dark:border-gum-gray-700 dark:text-gum-gray-200 dark:hover:bg-gum-gray-800"
              >
                Add {PLATFORM_LABEL[p]}
              </a>
            );
          })}
          <Info label="How points work across platforms">
            You earn points just by chatting — automatically, on every platform, whether you’re signed in here or not.
            Each platform keeps its own points and collection, so add every platform you watch on to see everything
            you’ve earned in one place. Switching here only changes which platform you’re viewing and spending from — it
            never changes what you earn.
          </Info>
          <Button variant="ghost" size="sm" disabled={busy} onClick={signOut}>
            Sign out
          </Button>
        </div>
      </div>
    </header>
  );
}

function TikTokLinkScreen({ username }) {
  const [busy, setBusy] = useState(false);

  // Completion poll — each poll also keeps the server-side pending match
  // armed; the bind lands server-side when the viewer's next chat message
  // arrives, and /api/viewer/state promotes the pending identity. A 401 or a
  // vanished linkPending means the pending window expired server-side —
  // reload so the server-rendered page sends the viewer back through OAuth.
  useEffect(() => {
    const poll = setInterval(async () => {
      try {
        const res = await fetch('/api/viewer/state');
        if (res.status === 401) {
          window.location.reload();
          return;
        }
        if (!res.ok) return;
        const data = await res.json();
        if (data?.identities?.includes('tiktok')) {
          window.location.reload();
          return;
        }
        if (!data?.linkPending) {
          window.location.reload();
          return;
        }
        setBusy(data.linkPending.busy === true);
      } catch {
        // Keep polling — transient failures are fine here.
      }
    }, 5000);
    return () => clearInterval(poll);
  }, []);

  return (
    <div className="space-y-4">
      <p className="text-sm leading-relaxed text-gum-gray-600 dark:text-gum-gray-400">
        TikTok doesn’t tell us your chat identity directly, so we spot it in chat instead. While a Shoosh streamer is{' '}
        <span className="font-semibold">live on TikTok</span>, just{' '}
        <span className="font-semibold">say anything in their chat</span> from{' '}
        <span className="font-semibold">@{username}</span> — any normal comment works, no special account or command
        needed.
      </p>
      <div className="flex items-center gap-3 rounded-xl border border-gum-gray-300 p-4 dark:border-gum-gray-700">
        <span className={`inline-block h-2 w-2 rounded-full ${busy ? 'bg-yellow-500' : 'animate-pulse bg-gum-pink'}`} />
        <span className="text-sm text-gum-gray-500">
          {busy
            ? 'Linking is busy right now — keep this page open, we’ll keep retrying.'
            : `Watching for a chat message from @${username}… this page updates automatically.`}
        </span>
      </div>
      <p className="text-xs leading-relaxed text-gum-gray-500">
        Your TikTok account is verified by the message itself (TikTok stamps the sender on every chat event) — nobody
        else can claim your points, and nothing extra ever shows in chat.
      </p>
    </div>
  );
}

/**
 * The channel switcher band (owner mockup-picked 2026-07-22, replacing the
 * purse-card strip — that row grew unboundedly with channel count and sat
 * asymmetric beside the fixed-size Catch card). Wallets are per-channel
 * (D4), so the control names the CHANNEL you're operating in: pink-outlined
 * (the app's active accent), with a WATCHING pill riding its corner when
 * the selection is the channel presence detected you in — the pill doubles
 * as visible feedback that the smart default worked. One wallet renders as
 * a static label (a dropdown with one option is a lie — no caret, no menu);
 * more open a menu of channel + balance rows, ✓ on the selection and the
 * WATCHING tag on the detected channel so "where am I actually" survives a
 * manual switch. Selection is the wallet Catch/bets spend from; it is
 * STICKY — only the page-load default ever comes from the suggestion.
 */
function ChannelSwitcher({ wallets, selectedChannel, suggestedChannel, onSelect }) {
  const [open, setOpen] = useState(false);
  // Same resolution rule as the parent's selectedWallet (find-or-null) so
  // the switcher can never highlight a channel the Catch panel treats as
  // unselected; the parent maintains "selectedChannel ∈ wallets or null".
  const selected = wallets.find((w) => w.channelUserId === selectedChannel) ?? null;
  const multi = wallets.length > 1;
  const watching = selected != null && selected.channelUserId === suggestedChannel;

  if (wallets.length === 0) {
    return (
      <Card
        eyebrow="Shoosh points"
        title="No points yet"
        desc="You earn automatically while you watch and chat — points start counting from your first message, before you ever sign in here."
      >
        <EmptyState>Watch a Shoosh stream and say hi in chat to start earning.</EmptyState>
      </Card>
    );
  }
  if (!selected) return null; // unreachable while the parent invariant holds
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-gum-gray-500">Points</span>
      <div className="relative">
        <button
          type="button"
          disabled={!multi}
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="relative flex items-center gap-2.5 rounded-xl border-[1.5px] border-gum-pink bg-gum-pink/10 px-3.5 py-2"
        >
          <span className="text-sm font-semibold text-gum-black dark:text-white">{selected.channelName}</span>
          <span className="text-xs text-gum-gray-500">{selected.balance.toLocaleString()} SP</span>
          {multi && <span className="text-[11px] text-gum-pink">{open ? '▴' : '▾'}</span>}
          {watching && (
            <span className="absolute -top-2 right-2.5 rounded-full bg-gum-pink px-2 py-px text-[9px] font-extrabold tracking-wide text-gum-black">
              WATCHING
            </span>
          )}
        </button>
        {open && (
          <>
            {/* click-away backdrop — below the menu, above the page; the
                established non-interactive idiom (controls.js dropdown), so
                keyboard order stays trigger → options with no phantom stop */}
            <span className="fixed inset-0 z-10" aria-hidden="true" onClick={() => setOpen(false)} />
            <div
              role="listbox"
              className="absolute left-0 top-full z-20 mt-1.5 min-w-[250px] rounded-xl border border-gum-gray-300 bg-white p-1.5 shadow-xl dark:border-gum-gray-700 dark:bg-gum-gray-800"
            >
              {wallets.map((w) => {
                const isSel = w.channelUserId === selected.channelUserId;
                const isWatching = w.channelUserId === suggestedChannel;
                return (
                  <button
                    key={w.channelUserId}
                    type="button"
                    role="option"
                    aria-selected={isSel}
                    onClick={() => {
                      onSelect(w.channelUserId);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-left text-sm ${
                      isSel ? 'bg-gum-pink/10' : 'hover:bg-gum-gray-100 dark:hover:bg-gum-gray-700'
                    }`}
                  >
                    <span className="font-medium text-gum-black dark:text-white">
                      {w.channelName}
                      {isSel && <span className="ml-1 font-bold text-gum-pink">✓</span>}
                      {isWatching && (
                        <span className="ml-1.5 rounded-full bg-gum-pink px-1.5 py-px align-[2px] text-[9px] font-extrabold tracking-wide text-gum-black">
                          WATCHING
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-gum-gray-500">{w.balance.toLocaleString()} SP</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
      <span className="text-xs text-gum-gray-500">
        {multi
          ? `${wallets.length} channels · you earn on each while you watch and chat`
          : 'You earn while you watch and chat — counting since your first message.'}
      </span>
    </div>
  );
}

function PullSurface({ price, fullPrice, isStarter, complete, selectedWallet, onCaught }) {
  const [pulling, setPulling] = useState(false);
  const [error, setError] = useState(null);

  const canAfford = selectedWallet && selectedWallet.balance >= price;
  const disabled = pulling || complete || !selectedWallet || !canAfford;

  async function doPull() {
    if (!selectedWallet) return;
    setPulling(true);
    setError(null);
    const { ok, status, data } = await postJson('/api/viewer/pull', { channelUserId: selectedWallet.channelUserId });
    setPulling(false);
    if (ok && data.ok) {
      onCaught({ characterKey: data.characterKey, revealed: data.revealed });
      return;
    }
    if (data.reason === 'insufficient') setError('Not enough points in that channel’s wallet.');
    else if (data.reason === 'conflict') setError('The draw collided with another pull — try again.');
    else if (data.reason === 'complete') setError('Collection complete — nothing left to catch!');
    else if (status === 400 && data.error === 'invalid_channel') setError('Pick a channel where you’ve earned points.');
    else setError('Something went wrong — try again.');
  }

  return (
    <Card
      className="h-full"
      eyebrow="Catch"
      title={complete ? 'Collection complete!' : `Catch a character — ${price.toLocaleString()} SP`}
      desc={
        complete
          ? 'You own the entire cast. Legend.'
          : isStarter
            ? `Your first catch is discounted (normally ${fullPrice.toLocaleString()} SP). Every catch is a character you don’t own yet — no duplicates, ever.`
            : 'Every catch is a character you don’t own yet — no duplicates, ever.'
      }
    >
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={doPull} disabled={disabled} loading={pulling} loadingText="Catching…">
          {complete ? 'Complete' : `Catch one · ${price.toLocaleString()} SP`}
        </Button>
        {!complete && selectedWallet && (
          <span className="text-xs text-gum-gray-500">
            Spending from {selectedWallet.channelName} ({selectedWallet.balance.toLocaleString()} SP)
          </span>
        )}
        {!complete && !selectedWallet && <span className="text-xs text-gum-gray-500">Pick a channel first.</span>}
        {!complete && selectedWallet && !canAfford && (
          <span className="text-xs text-yellow-500">Not enough points in this wallet yet.</span>
        )}
      </div>
      {error && <p className="mt-3 text-sm text-red-400">{error}</p>}
    </Card>
  );
}

/**
 * Your Character (owner design 2026-07-20; card rework mockup-picked
 * 2026-07-22, "variant B"): always shows the figure that walks out on
 * stream — the SELECTED character (chosen from the collection), or the
 * permanent AUTO-ASSIGNED one before any selection. The framed preview box
 * died (it read abstract): the character stands ON THE CARD — front view +
 * the rail view side by side, each on a soft floor shadow, over one subtle
 * shared floor line. The explainer lives in the ⓘ (the controls.js Info
 * convention), Randomize rides the header corner, and the card fills its
 * grid row (h-full + the stage's flex-1) so the Catch pairing stays even.
 */
function YourCharacter({ ownedSet, selectedKey, defaultKey, busy, error, onSelect }) {
  const owned = CAST.filter((c) => ownedSet.has(c.id));
  const currentKey = selectedKey || defaultKey;
  const current = currentKey ? characterById(currentKey) : null;
  const isSelected = Boolean(selectedKey);

  function randomize() {
    if (owned.length === 0) return;
    // Guarantee a visible change when there's anything to change to — filter
    // by the DISPLAYED character (selected OR the auto-assigned default),
    // which is owned either way now that defaults are granted.
    const pool = owned.length > 1 ? owned.filter((c) => c.id !== currentKey) : owned;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    onSelect(pick.id);
  }

  const shadow = (
    <span className="absolute -bottom-px h-3 w-3/4 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.45),transparent_70%)]" />
  );

  return (
    <Card
      className="flex h-full flex-col"
      eyebrow="Your character"
      title={
        <>
          {current ? `${isSelected ? 'Selected' : 'Auto-assigned'} — ${displayNameFor(current.id)}` : 'Your character'}
          <Info label="About your character">
            The figure that walks out on stream when you chat — the same one on every Shoosh channel. Select one by
            clicking a caught character in your collection below; Randomize picks one of your caught characters at
            random (catch more to unlock it).
          </Info>
        </>
      }
      aside={
        /* <= 1: with only the auto-assigned default owned, a randomize could
           only re-pick the displayed character — pointless write. */
        <Button variant="secondary" size="sm" disabled={busy || owned.length <= 1} onClick={randomize}>
          Randomize
        </Button>
      }
    >
      {current && (
        <>
          <div className="flex flex-1 items-end gap-7 px-1">
            <span className="relative flex items-end justify-center">
              {shadow}
              <img
                src={view(current, 'front')}
                alt={`${displayNameFor(current.id)} — front view`}
                className="relative z-[1] h-[86px] w-auto"
              />
            </span>
            <span className="relative flex items-end justify-center">
              {shadow}
              <img
                src={view(current, 'back')}
                alt={`${displayNameFor(current.id)} — as they stand on the overlay rail`}
                className="relative z-[1] h-[78px] w-auto"
              />
            </span>
          </div>
          {/* the shared floor line their feet rest on — "on stage", no box */}
          <div className="mt-px border-t border-gum-gray-400/25" />
        </>
      )}
      {error && <p className="mt-3 text-sm text-red-400">{error}</p>}
    </Card>
  );
}

function CollectionGrid({ ownedSet, selectedKey, ownedCount, castTotal, busy, onSelect }) {
  const pct = castTotal > 0 ? Math.round((ownedCount / castTotal) * 100) : 0;
  return (
    <Card
      eyebrow="Collection"
      title={`${ownedCount} of ${castTotal} caught`}
      aside={<span className="text-xs font-semibold text-gum-gray-500">{pct}%</span>}
    >
      <div className="mb-4 h-1.5 overflow-hidden rounded-full bg-gum-gray-200 dark:bg-gum-gray-700">
        <div className="h-full rounded-full bg-gum-pink transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-9">
        {CAST.map((c) => {
          const owned = ownedSet.has(c.id);
          const selected = selectedKey === c.id;
          const tile = (
            <>
              <div className="flex h-16 w-full items-end justify-center">
                {/* Unowned = dark silhouette (mystery — no names, no prices). */}
                <img
                  src={view(c, 'front')}
                  alt={owned ? displayNameFor(c.id) : 'Uncaught character'}
                  className={`max-h-16 w-auto ${owned ? '' : 'opacity-30 brightness-0 dark:opacity-40'}`}
                />
              </div>
              {selected && (
                <span
                  className="absolute -right-1 -top-1 rounded-full bg-gum-pink px-1.5 text-[10px] font-bold text-gum-black"
                  title="Your selected character"
                >
                  ★
                </span>
              )}
              <span className="mt-1 max-w-full truncate text-[10px] text-gum-gray-500">
                {owned ? displayNameFor(c.id) : '???'}
              </span>
            </>
          );
          return owned ? (
            <button
              key={c.id}
              type="button"
              disabled={busy}
              onClick={() => onSelect(selected ? null : c.id)}
              aria-pressed={selected}
              title={
                selected
                  ? 'Click to go back to your auto-assigned character'
                  : `Make ${displayNameFor(c.id)} your stream character`
              }
              className={`relative flex flex-col items-center rounded-xl border p-1.5 transition-colors ${
                selected
                  ? 'border-gum-pink bg-gum-pink/10'
                  : 'border-transparent hover:border-gum-gray-300 dark:hover:border-gum-gray-700'
              }`}
            >
              {tile}
            </button>
          ) : (
            <div key={c.id} className="relative flex flex-col items-center p-1.5">
              {tile}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function RevealModal({ characterKey, revealed, onClose }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  const character = characterById(characterKey);
  if (!character) return null;

  return (
    <Modal onClose={onClose} ariaLabel="Character caught" className="w-full max-w-sm">
      <div className="rounded-2xl border border-emerald-400/40 bg-gum-white p-6 text-center shadow-xl dark:bg-gum-gray-800">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-400">★ Caught!</div>
        <div className="mt-4 flex h-40 items-end justify-center">
          <img
            src={view(character, 'front')}
            alt={displayNameFor(character.id)}
            className="max-h-40 w-auto transition-all duration-700 ease-out"
            style={{
              opacity: shown ? 1 : 0,
              transform: shown ? 'rotateY(0deg) scale(1)' : 'rotateY(90deg) scale(0.7)',
            }}
          />
        </div>
        <div className="mt-3 text-lg font-bold text-gum-black dark:text-white">{displayNameFor(character.id)}</div>
        {revealed && <div className="mt-1 text-xs text-emerald-400">Revealed live on stream ✨</div>}
        <Button variant="secondary" size="sm" className="mt-5" onClick={onClose}>
          Nice
        </Button>
      </div>
    </Modal>
  );
}

function DangerZone({ platform }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function handleDelete() {
    if (confirm !== CONFIRM_PHRASE) {
      setError(`Type "${CONFIRM_PHRASE}" exactly to confirm.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    const { ok, data } = await postJson('/api/viewer/delete', { confirm });
    if (ok && data.ok) {
      if (!data.remaining || data.remaining.length === 0) window.location.href = '/viewer';
      else window.location.reload();
      return;
    }
    setError(
      data.error === 'confirm_mismatch' ? `Type "${CONFIRM_PHRASE}" exactly to confirm.` : 'Delete failed — try again.',
    );
    setSubmitting(false);
  }

  // Red-accented bespoke card — mirrors the dashboard privacy panel's
  // DangerZone treatment (the danger eyebrow the neutral Card can't carry).
  return (
    <section className="rounded-2xl border border-red-500/30 bg-gum-white p-5 shadow-sm dark:bg-gum-gray-800 sm:p-6">
      <div className={`flex items-center gap-2 ${open ? 'mb-4' : ''}`}>
        <span className="h-3 w-1 rounded-full bg-red-500" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-red-500">Danger zone</span>
      </div>
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-sm font-bold text-red-500 hover:text-red-400"
        >
          Show data deletion controls &rarr;
        </button>
      ) : (
        <div className="space-y-3">
          <div>
            <div className="text-sm font-semibold text-gum-black dark:text-white">Delete my data</div>
            <p className="mt-1 text-sm text-gum-gray-600 dark:text-gum-gray-400">
              Permanently deletes your points, earning history, and character collection for the{' '}
              <span className="font-semibold">{PLATFORM_LABEL[platform]}</span> account you’re signed in as — across
              every Shoosh channel, immediately, with no recovery. Other platform identities keep their own separate
              collections.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-xs text-gum-gray-600 dark:text-gum-gray-400">
              Type{' '}
              <span className="font-mono font-bold text-gum-gray-900 dark:text-gum-gray-100">{CONFIRM_PHRASE}</span> to
              confirm
            </label>
            <input
              type="text"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-lg border border-gum-gray-300 bg-gum-gray-100 px-3 py-2 text-sm text-gum-gray-900 focus:border-red-400 focus:outline-none dark:border-gum-gray-700 dark:bg-gum-gray-900 dark:text-gum-white"
            />
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex gap-2">
            <Button
              variant="danger"
              className="flex-1"
              onClick={handleDelete}
              disabled={confirm !== CONFIRM_PHRASE}
              loading={submitting}
              loadingText="Deleting…"
            >
              Delete my data
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setOpen(false);
                setConfirm('');
                setError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

// ── §8.6 Active polls & gambas (E5) ─────────────────────────────────────────

function usePollCountdown(poll) {
  const [now, setNow] = useState(() => Date.now());
  const [endsAt, setEndsAt] = useState(null);
  useEffect(() => {
    setEndsAt(poll.endsInMs != null ? Date.now() + poll.endsInMs : null);
    setNow(Date.now());
  }, [poll]);
  useEffect(() => {
    if (poll.status !== 'open' || endsAt == null) return undefined;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [poll.status, endsAt]);
  if (poll.status !== 'open' || endsAt == null) return null;
  const s = Math.max(0, Math.ceil((endsAt - now) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

const POLL_REASON_TEXT = {
  insufficient: 'Not enough Shoosh Points in this channel.',
  already_voted: 'Your vote is in — votes are final.',
  wrong_side: 'Your wager is locked to your outcome — you can add to it, not switch.',
  over_max: 'That would pass the 250,000 SP cap.',
  closed: 'This one just closed.',
  no_poll: 'This one just closed.',
  not_live: 'The stream session ended — this event is wrapping up.',
  wrong_mode: 'This event doesn’t take that action.',
  invalid: 'That didn’t work — check the amount.',
  error: 'Something went wrong — try again.',
};

/** One live event: vote/pick buttons, and the SP controls (boost or bet) when
 *  the event takes money. All server-validated; inline reasons only. */
function ActivePollCard({ poll, onActed }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [amount, setAmount] = useState('');
  const [pickedIndex, setPickedIndex] = useState(null);
  const countdown = usePollCountdown(poll);

  const isBetting = poll.kind === 'gamba' && poll.bettingEnabled;
  const isFreeGamba = poll.kind === 'gamba' && !poll.bettingEnabled;
  const open = poll.status === 'open';
  const my = poll.myEntry;
  const myVote = my?.optionIndex ?? null;
  const totalVotes = poll.tallies.reduce((s, t) => s + t.votes + t.paidVotes, 0);

  async function act(url, body) {
    setBusy(true);
    setError(null);
    const { data } = await postJson(url, body);
    setBusy(false);
    if (data?.ok) onActed();
    else setError(POLL_REASON_TEXT[data?.reason] || POLL_REASON_TEXT.error);
  }

  const vote = (optionIndex) =>
    act('/api/viewer/vote', { channelUserId: poll.channelUserId, pollId: poll.pollId, optionIndex });
  const spend = (optionIndex, sp) =>
    act('/api/viewer/bet', { channelUserId: poll.channelUserId, pollId: poll.pollId, optionIndex, amount: sp });

  const spendTarget = isBetting && my?.amount > 0 ? myVote : pickedIndex;
  const spendAmount = Number(amount);
  const canSpend = open && spendTarget != null && Number.isInteger(spendAmount) && spendAmount > 0 && !busy;

  return (
    <Card
      eyebrow={`${poll.kind === 'gamba' ? 'Gamba' : 'Poll'} · ${poll.channelName}`}
      title={poll.question}
      desc={
        poll.status === 'locked' ? 'Locked — waiting on the result.' : countdown ? `Closes in ${countdown}` : 'Open'
      }
      accent
    >
      <div className="space-y-3">
        <div className="space-y-2">
          {poll.options.map((label, i) => {
            const t = poll.tallies[i] || { votes: 0, paidVotes: 0, sp: 0 };
            const share = isBetting
              ? poll.totalPot > 0
                ? t.sp / poll.totalPot
                : 0
              : totalVotes > 0
                ? (t.votes + t.paidVotes) / totalVotes
                : 0;
            const mine = myVote === i;
            const targeted = spendTarget === i;
            const ratio = poll.ratios?.[i];
            const freeVoteTaken = my != null && myVote != null;
            return (
              <button
                key={i}
                type="button"
                disabled={busy || !open}
                onClick={() => {
                  if (isBetting) {
                    if (!(my?.amount > 0)) setPickedIndex(i);
                  } else if (!freeVoteTaken) {
                    vote(i);
                  } else if (poll.kind === 'poll' && poll.boostCostSp != null) {
                    setPickedIndex(i);
                  }
                }}
                className={`w-full rounded-xl border p-2.5 text-left transition-colors ${
                  mine || targeted
                    ? 'border-gum-pink bg-gum-pink/10'
                    : 'border-gum-gray-300 dark:border-gum-gray-700 hover:border-gum-pink/60'
                } ${!open ? 'opacity-70' : ''}`}
              >
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="font-semibold truncate">
                    {label}
                    {mine ? (
                      <span className="text-gum-pink">
                        {' '}
                        ✓{my.amount > 0 ? ` ${my.amount.toLocaleString()} SP` : ''}
                      </span>
                    ) : null}
                  </span>
                  <span className="shrink-0 tabular-nums text-xs text-gum-gray-500 dark:text-gum-gray-400">
                    {isBetting ? (
                      <>
                        <span className="font-bold text-gum-pink mr-1.5">
                          {ratio != null ? `1:${ratio >= 10 ? Math.round(ratio) : ratio.toFixed(2)}` : '—'}
                        </span>
                        {t.sp.toLocaleString()} SP
                      </>
                    ) : (
                      `${t.votes + t.paidVotes}${t.paidVotes > 0 ? ` (+${t.paidVotes} paid)` : ''}`
                    )}
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gum-gray-200 dark:bg-gum-gray-800">
                  <div
                    className="h-full rounded-full bg-gum-pink transition-[width] duration-700"
                    style={{ width: `${Math.round(share * 100)}%` }}
                  />
                </div>
              </button>
            );
          })}
        </div>

        {open && isBetting ? (
          <div className="flex items-center gap-2">
            <input
              type="number"
              inputMode="numeric"
              min="10"
              placeholder={my?.amount > 0 ? 'Add to your wager (SP)' : 'Wager (SP, min 10)'}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-44 rounded-lg border border-gum-gray-300 bg-transparent px-3 py-1.5 text-sm dark:border-gum-gray-700"
            />
            <Button size="sm" disabled={!canSpend} onClick={() => spend(spendTarget, spendAmount)}>
              {my?.amount > 0 ? 'Add to bet' : 'Place bet'}
            </Button>
            {my?.amount > 0 ? null : (
              <span className="text-xs text-gum-gray-500">
                {pickedIndex == null ? 'Pick an outcome first' : `On “${poll.options[pickedIndex]}”`}
              </span>
            )}
          </div>
        ) : null}

        {open && poll.kind === 'poll' && poll.boostCostSp != null ? (
          <div className="flex items-center gap-2">
            <input
              type="number"
              inputMode="numeric"
              min={poll.boostCostSp}
              placeholder={`Extra votes — ${poll.boostCostSp.toLocaleString()} SP each`}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-56 rounded-lg border border-gum-gray-300 bg-transparent px-3 py-1.5 text-sm dark:border-gum-gray-700"
            />
            <Button size="sm" disabled={!canSpend} onClick={() => spend(spendTarget, spendAmount)}>
              Boost
            </Button>
            <span className="text-xs text-gum-gray-500">
              {spendTarget != null
                ? `On “${poll.options[spendTarget]}”`
                : myVote != null
                  ? 'Tap an option to boost it'
                  : 'Vote first, or tap an option'}
            </span>
          </div>
        ) : null}

        {isFreeGamba && open && myVote == null ? (
          <div className="text-xs text-gum-gray-500">Pick free — a correct pick earns {poll.winAwardSp ?? 0} SP.</div>
        ) : null}
        {isBetting && poll.totalPot > 0 ? (
          <div className="text-xs text-gum-gray-500">
            Pot <b className="text-gum-black dark:text-gum-white">{poll.totalPot.toLocaleString()} SP</b>
          </div>
        ) : null}
        {error ? <div className="text-sm text-red-400">{error}</div> : null}
      </div>
    </Card>
  );
}

// ── Your Record (E5 analytics — derived, per-identity, cross-channel) ───────

function YourRecord({ stats }) {
  const tiles = [
    { label: 'Record', value: `${stats.wins}–${stats.losses}` },
    { label: 'Streak', value: `${stats.currentStreak}${stats.bestStreak > 0 ? ` (best ${stats.bestStreak})` : ''}` },
    { label: 'SP won', value: stats.spWon.toLocaleString() },
    { label: 'SP wagered', value: stats.spWagered.toLocaleString() },
    { label: 'Biggest win', value: stats.biggestWin.toLocaleString() },
    { label: 'Polls voted', value: String(stats.pollsVoted) },
  ];
  return (
    <Card eyebrow="Your record" title="Polls & gambas" desc="Across every Shoosh channel, under this identity.">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
          {tiles.map((t) => (
            <div key={t.label} className="rounded-xl border border-gum-gray-300 p-3 dark:border-gum-gray-700">
              <div className="text-[11px] font-bold uppercase tracking-wide text-gum-gray-500">{t.label}</div>
              <div className="mt-0.5 text-lg font-bold tabular-nums">{t.value}</div>
            </div>
          ))}
        </div>
        {stats.last.length > 0 ? (
          <div className="space-y-1.5">
            <div className="text-[11px] font-bold uppercase tracking-wide text-gum-gray-500">Recent results</div>
            {stats.last.map((r, i) => (
              <div key={i} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="truncate text-gum-gray-600 dark:text-gum-gray-300">{r.question}</span>
                <span className="shrink-0 tabular-nums text-xs">
                  {r.kind === 'gamba' ? (
                    r.won ? (
                      <span className="font-bold text-emerald-500">
                        WON{r.payout > 0 ? ` +${r.payout.toLocaleString()}` : ''}
                      </span>
                    ) : (
                      <span className="text-red-400">LOST</span>
                    )
                  ) : (
                    <span className="text-gum-gray-500">{r.outcome ? `“${r.outcome}” won` : 'Tie'}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
